import { useState, useEffect, useMemo } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { specificFundingPolicyTypeDefList } from '@shared';

const featureFlagDefinitions: { key: FeatureFlagKeys; label: string; description: string }[] = [
  {
    key: 'wizardMode',
    label: 'Wizard Mode',
    description: 'Grant temporary wizard/admin access to this advisor',
  },
  { key: 'vbShop', label: 'VB Shop', description: 'Group policy funding page' },
  { key: 'ltciPartners', label: 'LTCI Partners', description: 'LTCI Partners disclaimers' },
  { key: 'agis', label: 'AGIS', description: 'AGIS group policy funding page' },
  {
    key: 'quoteApplyLincolnFinancial',
    label: 'Quote Apply Lincoln Financial',
    description: 'Quote Apply Lincoln Financial',
  },
  {
    key: 'quoteApplyNewYorkLife',
    label: 'Quote Apply New York Life',
    description: 'Quote Apply New York Life',
  },
  {
    key: 'hideSinglePayAnd10Pay',
    label: 'Hide Single Pay and 10-Pay',
    description: 'Hide Single Pay and 10-Pay payment options in Shop Insurance filters',
  },
  {
    key: 'd2cMode',
    label: 'D2C Mode',
    description: 'Enable direct-to-consumer mode for this advisor\'s clients',
  },
  {
    key: 'edjPilotAdvisor',
    label: 'EDJ Pilot Advisor',
    description: 'Enable EDJ pilot advisor features',
  },
  {
    key: 'edjAdvisorPilotIntakeForm',
    label: 'EDJ Advisor Pilot Intake Form',
    description: 'Enable self-test intake form for EDJ pilot advisors',
  },
  {
    key: 'ccrcTypeAProducts',
    label: 'CCRC Type A Products',
    description: 'Enable Continuing Care Retirement Community Type A product modeling',
  },
  {
    key: 'financialFeasibility',
    label: 'Financial Feasibility',
    description: 'Enable the Financial Feasibility analysis in guided planning',
  },
  {
    key: 'freemiumAdvisor',
    label: 'Freemium Advisor',
    description:
      'Remove the subscription paywall at sign-up and enable the usage paywall at the financial planning page',
  },
];

interface FeatureFlagsEditorProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
}

export function FeatureFlagsEditor({ value, onChange, error }: FeatureFlagsEditorProps) {
  const [flags, setFlags] = useState<FeatureFlags>({});

  useEffect(() => {
    try {
      const parsed = value ? JSON.parse(value) : {};
      setFlags(parsed);
    } catch {
      setFlags({});
    }
  }, [value]);

  const handleToggle = (key: FeatureFlagKeys) => {
    const newFlags = { ...flags, [key]: !flags[key] };
    setFlags(newFlags);
    onChange(JSON.stringify(newFlags));
  };

  const disabledProducts = useMemo(
    () => new Set(flags.disabledFundingPolicyTypes ?? []),
    [flags.disabledFundingPolicyTypes],
  );

  const productsByCarrier = useMemo(() => {
    const grouped: Record<string, typeof specificFundingPolicyTypeDefList> = {};
    for (const product of specificFundingPolicyTypeDefList) {
      const carrier = product.carrier || 'Other';
      if (!grouped[carrier]) grouped[carrier] = [];
      grouped[carrier].push(product);
    }
    return grouped;
  }, []);

  const handleProductToggle = (productId: number) => {
    const currentDisabled = flags.disabledFundingPolicyTypes ?? [];
    const isDisabled = currentDisabled.includes(productId);
    const newDisabled = isDisabled
      ? currentDisabled.filter(id => id !== productId)
      : [...currentDisabled, productId];

    const newFlags: FeatureFlags = { ...flags };
    if (newDisabled.length > 0) {
      newFlags.disabledFundingPolicyTypes = newDisabled;
    } else {
      delete newFlags.disabledFundingPolicyTypes;
    }
    setFlags(newFlags);
    onChange(JSON.stringify(newFlags));
  };

  return (
    <Card className={cn('space-y-4 p-4', error && 'border-red-500')}>
      <div className="space-y-3">
        {featureFlagDefinitions.map(({ key, label, description }) => (
          <div
            key={key}
            className="flex items-center justify-between space-x-3"
          >
            <div className="flex-1">
              <Label
                htmlFor={key}
                className="text-sm font-medium"
              >
                {label}
              </Label>
              <p className="text-xs text-gray-700">{description}</p>
            </div>
            <Switch
              id={key}
              checked={!!flags[key]}
              onCheckedChange={() => handleToggle(key)}
            />
          </div>
        ))}
      </div>

      <Collapsible className="border-t pt-4">
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <div className="text-left">
            <h4 className="text-sm font-medium">Product Access</h4>
            <p className="text-xs text-gray-500">
              Toggle off to hide a product from this advisor.
              {disabledProducts.size > 0 && (
                <span className="ml-1 font-medium text-red-600">
                  ({disabledProducts.size} disabled)
                </span>
              )}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-gray-500 transition-transform duration-200 [[data-state=open]>&]:rotate-90" />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          {Object.entries(productsByCarrier).map(([carrier, products]) => (
            <div
              key={carrier}
              className="space-y-2"
            >
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{carrier}</p>
              {products.map(product => (
                <div
                  key={product.value}
                  className="flex items-center justify-between space-x-3"
                >
                  <Label
                    htmlFor={`product-${product.value}`}
                    className="text-sm"
                  >
                    {product.label}
                  </Label>
                  <Switch
                    id={`product-${product.value}`}
                    checked={!disabledProducts.has(product.value)}
                    onCheckedChange={() => handleProductToggle(product.value)}
                  />
                </div>
              ))}
            </div>
          ))}
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
